// frontend/src/components/Requisitions/edit/components/EditStepItems.jsx

import React, { useMemo, useState } from "react";

import LoadingSpinner from "../../../UI/LoadingSpinner";
import NoTranslateText from "../../../UI/NoTranslateText";
import RequisitionQuotesPanel from "../../RequisitionQuotesPanel";

import ManualUomWarning from "./ManualUomWarning";
import RealAmountSection from "./RealAmountSection";

import {
  fmtMoney,
  getNiceDescDisplay,
  getProductLabel,
  getUnitLabel,
  labelFrom,
} from "../utils";

const EMPTY_ITEM_FORM = {
  _cid: null,
  id: null,
  product: "",
  product_display: "",
  quantity: "",
  unit: "",
  unit_display: "",
  description: "",
  description_text: "",
  description_display: "",
  manual_description: "",
  estimated_unit_cost: "",
  estimated_total: "",
};

const normalizeSearchText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function EditStepItems({
  requisitionId,
  manualItemsMode,
  currentUnitLabel,
  loadingCatalogs,
  showRealAmountSection,
  realAmountNeedsUpdate,
  realAmountCaptured,

  form,
  setForm,
  setFormPatched,
  baseEnabled,
  effectiveFormProductId,
  currentTenderId,
  isProductLocked,
  filteredProducts,
  displayDescOptions,
  displayUnits,
  manualHistoryOptions,
  loadingManualHistory,
  ensureManualHistoryLoaded,
  busyItemOp,
  handleDescriptionChange,
  handleManualHistorySelection,
  addOrUpdateItem,

  openClassifier,
  openCatalogModal,
  openRegisterModal,
  showRegisterProductButton,

  items,
  products,
  units,
  descCache,
  editRow,
  deleteRow,

  showQuotesAndAck,
  onDraftInvalidChange,
  ackCostRealistic,
  onAckCostRealisticChange,

  realAmountBase,
  realUnitById,
  realTotalsById,
  handleRealUnitChange,
  handleRealUnitBlur,
  qtyOf,
  estimatedUnitOf,
  estimatedTotalOf,
  realRowTotalOf,
  realTotalSum,
  realAmountReason,
  onRealAmountReasonChange,
  busyRealAmount,
  disableRealAmountByAck,
  disableRealByQuoteDraft,
  onSaveRealTotals,
  realAmountLogs,

  onPrev,
  onCancel,
  busySave,
  disableSave,
  disableSaveByAck,
  onSaveAll,

  canEditRequisition = false,
  canEditHeader = false,
  canManageRealAmounts = false,
  editBlockedMessage = "",
  readOnly = false,
}) {
  const isReadOnly = Boolean(readOnly || !canEditRequisition);
  const disableItemForm = isReadOnly || busyItemOp;
  const disableTableActions = isReadOnly || busyItemOp;
  const disableAckCheckbox = isReadOnly;
  const disableQuotesPanel = isReadOnly;
  const disableRealSection = isReadOnly || !canManageRealAmounts;
  const disablePrimarySave = busySave || disableSave || disableSaveByAck || isReadOnly;
  const [manualHistoryMenuOpen, setManualHistoryMenuOpen] = useState(false);

  const filteredManualHistoryOptions = useMemo(() => {
    const query = normalizeSearchText(form.manual_description);
    const base = Array.isArray(manualHistoryOptions) ? manualHistoryOptions : [];
    const filtered = query
      ? base.filter((entry) =>
          normalizeSearchText(entry?.manual_description || "").includes(query)
        )
      : base;

    return filtered.slice(0, 8);
  }, [form.manual_description, manualHistoryOptions]);

  const shouldShowManualHistoryDropdown = useMemo(
    () =>
      Boolean(
        manualItemsMode &&
          effectiveFormProductId &&
          manualHistoryMenuOpen &&
          normalizeSearchText(form.manual_description) &&
          filteredManualHistoryOptions.length > 0
      ),
    [
      effectiveFormProductId,
      filteredManualHistoryOptions.length,
      form.manual_description,
      manualHistoryMenuOpen,
      manualItemsMode,
    ]
  );

  const saveButtonTitle = isReadOnly
    ? (editBlockedMessage || "No tienes permisos para modificar esta requisición.")
    : disableSaveByAck
      ? 'Debes confirmar "costo aproximado pero realista" para poder guardar.'
      : disableSave
        ? "Debes capturar el monto real por partida antes de guardar cambios."
        : "";

  return (
    <section className="bg-white rounded-xl shadow p-4 md:p-6">
      <h2 className="text-lg font-semibold mb-4">Registro de Partidas</h2>

      {!!editBlockedMessage && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-800 rounded p-3 text-sm">
          <div className="font-semibold">Edición bloqueada</div>
          <div className="mt-1">{editBlockedMessage}</div>
        </div>
      )}

      {isReadOnly && (
        <div className="mb-4 border border-slate-300 bg-slate-50 text-slate-800 rounded p-3 text-sm">
          <div className="font-semibold">Paso 2 en modo solo lectura</div>
          <div className="mt-1">
            Puedes consultar partidas, cotizaciones e historial, pero no modificar esta requisición desde aquí.
          </div>
        </div>
      )}

      {!isReadOnly && !canEditHeader && canEditRequisition && (
        <div className="mb-4 border border-blue-200 bg-blue-50 text-blue-900 rounded p-3 text-sm">
          <div className="font-semibold">Edición parcial habilitada</div>
          <div className="mt-1">
            Puedes trabajar con partidas en este paso, aunque los campos del encabezado del Paso 1 permanecen en solo lectura.
          </div>
        </div>
      )}

      {manualItemsMode ? <ManualUomWarning unitLabel={currentUnitLabel} /> : null}

      {loadingCatalogs ? (
        <LoadingSpinner />
      ) : (
        <>
          {showRealAmountSection && realAmountNeedsUpdate && !realAmountCaptured && !isReadOnly && (
            <div className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded p-3 text-sm">
              <div className="font-semibold">No puedes guardar cambios todavía.</div>
              <div className="mt-1">
                Debes capturar el <b>monto real por partida</b> (con auditoría) antes de guardar la requisición.
              </div>
              <div className="mt-2 text-[12px] text-amber-800">
                Nota: los montos reales pueden diferir del estimado. Verifica facturas / ajustes antes de guardar.
              </div>
            </div>
          )}

          <form onSubmit={addOrUpdateItem} className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Objeto del Gasto
              </label>
              <select
                className="w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={effectiveFormProductId}
                disabled={disableItemForm || isProductLocked || (!manualItemsMode && !currentTenderId)}
                title={
                  isReadOnly
                    ? "Esta requisición está en modo solo lectura."
                    : isProductLocked
                      ? "El objeto del gasto quedó fijado por la primera partida de esta requisición."
                      : !manualItemsMode && !currentTenderId
                        ? "Selecciona primero una licitación."
                        : ""
                }
                onChange={(e) => {
                  if (isReadOnly || isProductLocked) return;

                  setFormPatched({
                    product: e.target.value,
                    product_display: labelFrom(
                      filteredProducts,
                      e.target.value,
                      getProductLabel
                    ),
                    description: "",
                    description_text: "",
                    description_display: "",
                    manual_description: "",
                    estimated_unit_cost: "",
                    estimated_total: "",
                    quantity: "",
                    unit: "",
                    unit_display: "",
                  });

                  if (manualItemsMode && e.target.value) {
                    ensureManualHistoryLoaded?.(e.target.value);
                  }
                  setManualHistoryMenuOpen(false);
                }}
              >
                <option value="">
                  {!manualItemsMode && !currentTenderId
                    ? "Selecciona primero la Licitación"
                    : "— Selecciona —"}
                </option>
                {filteredProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {getProductLabel(product)}
                  </option>
                ))}
              </select>

              {isProductLocked && (
                <div className="text-[11px] text-gray-500 mt-1">
                  El objeto del gasto quedó fijado por la primera partida de esta requisición.
                </div>
              )}
            </div>

            <div className="md:col-span-8">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Descripción
              </label>

              {manualItemsMode ? (
                <>
                <input
                  className="w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed notranslate"
                  value={form.manual_description}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setFormPatched({ manual_description: nextValue });
                    ensureManualHistoryLoaded?.();
                    setManualHistoryMenuOpen(Boolean(normalizeSearchText(nextValue)));
                  }}
                  onFocus={() => {
                    ensureManualHistoryLoaded?.();
                    setManualHistoryMenuOpen(Boolean(normalizeSearchText(form.manual_description)));
                  }}
                  onBlur={() => {
                    window.setTimeout(() => {
                      setManualHistoryMenuOpen(false);
                    }, 120);
                  }}
                  disabled={disableItemForm || !effectiveFormProductId}
                  translate="no"
                  placeholder={
                    effectiveFormProductId
                      ? "Escribe la descripción (manual)"
                      : "Selecciona Objeto del Gasto primero"
                  }
                />
                {loadingManualHistory && effectiveFormProductId ? (
                  <div className="mt-1 text-[11px] text-gray-500">Cargando historial...</div>
                ) : null}
                {shouldShowManualHistoryDropdown ? (
                  <div className="mt-1 rounded-md border border-slate-200 bg-white shadow-lg">
                    <div className="max-h-48 overflow-y-auto py-1">
                      {filteredManualHistoryOptions.map((entry) => {
                        const key = [
                          entry.manual_description || "",
                          entry.unit || "",
                          entry.estimated_unit_cost || "",
                          entry.source_requisition_id || "",
                        ].join("|");

                        return (
                          <button
                            key={key}
                            type="button"
                            className="block w-full px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleManualHistorySelection(entry);
                              setManualHistoryMenuOpen(false);
                            }}
                          >
                            <NoTranslateText
                              className="font-medium text-sm text-slate-900 block"
                              text={entry.manual_description}
                            />
                            <div className="mt-1 text-[11px] text-slate-500">
                              Cantidad {entry.quantity || "—"} · {entry.unit_display || "Sin unidad"} · $
                              {fmtMoney(entry.estimated_unit_cost)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                </>
              ) : (
                <select
                  className="w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={form.description}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  disabled={disableItemForm || !effectiveFormProductId || !currentTenderId}
                >
                  <option value="">
                    {effectiveFormProductId
                      ? "— Selecciona —"
                      : "Selecciona Objeto del Gasto primero"}
                  </option>
                  {displayDescOptions.map((desc) => (
                    <option key={desc.id} value={desc.id}>
                      {desc.text}
                      {desc.unitLabel ? ` — ${desc.unitLabel}` : ""}
                      {desc.estimated_unit_cost
                        ? ` — $${fmtMoney(desc.estimated_unit_cost)}`
                        : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Cantidad
              </label>
              <input
                type="number"
                min="1"
                className="w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={form.quantity}
                onChange={(e) => setFormPatched({ quantity: e.target.value })}
                disabled={disableItemForm || !baseEnabled}
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Unidad
              </label>
              <select
                className={`w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  manualItemsMode ? "" : "bg-gray-100"
                }`}
                value={form.unit}
                onChange={(e) =>
                  setFormPatched({
                    unit: e.target.value,
                    unit_display: labelFrom(
                      displayUnits,
                      e.target.value,
                      getUnitLabel
                    ),
                  })
                }
                disabled={disableItemForm || !manualItemsMode || !effectiveFormProductId}
              >
                <option value="">— Selecciona —</option>
                {displayUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {getUnitLabel(unit)}
                  </option>
                ))}
              </select>

              <div className="text-[11px] text-gray-500 mt-1">
                {manualItemsMode
                  ? "Selecciona manualmente la unidad."
                  : "Se llena automáticamente desde la descripción."}
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Costo unitario
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`w-full border rounded px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  manualItemsMode ? "" : "bg-gray-100"
                }`}
                value={form.estimated_unit_cost}
                readOnly={!manualItemsMode}
                disabled={disableItemForm || !baseEnabled}
                onChange={(e) => {
                  if (isReadOnly || !manualItemsMode) return;
                  setFormPatched({ estimated_unit_cost: e.target.value });
                }}
                placeholder="0.00"
              />

              <div className="text-[11px] text-gray-500 mt-1">
                {manualItemsMode
                  ? "Captura manualmente el costo unitario."
                  : "Se llena automáticamente desde la descripción."}
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Monto estimado (total)
              </label>
              <input
                type="number"
                className="w-full border rounded px-2 py-1 bg-gray-100"
                value={form.estimated_total}
                readOnly
                placeholder="0.00"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Se calcula automáticamente (cantidad × unitario).
              </div>
            </div>

            <div className="md:col-span-12 flex items-end gap-2">
              <button
                type="submit"
                disabled={disableItemForm}
                className={`px-3 py-2 rounded text-white ${
                  disableItemForm ? "bg-green-300" : "bg-green-600 hover:bg-green-700"
                }`}
                title={isReadOnly ? (editBlockedMessage || "Modo solo lectura.") : ""}
              >
                {busyItemOp ? "Guardando..." : form._cid ? "Guardar cambios" : "Agregar"}
              </button>

              {form._cid && (
                <button
                  type="button"
                  className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100"
                  onClick={() => setForm(EMPTY_ITEM_FORM)}
                  disabled={disableItemForm}
                >
                  Cancelar
                </button>
              )}

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openClassifier();
                  }}
                  className="px-3 py-2 rounded bg-slate-200 hover:bg-slate-300"
                >
                  Clasificador
                </button>

                {!manualItemsMode && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCatalogModal();
                      }}
                      className="px-3 py-2 rounded bg-slate-200 hover:bg-slate-300"
                    >
                      Ver Catálogo
                    </button>

                    {showRegisterProductButton ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openRegisterModal();
                        }}
                        disabled={isReadOnly}
                        className={`px-3 py-2 rounded text-white ${
                          isReadOnly
                            ? "bg-blue-300"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                        title={isReadOnly ? (editBlockedMessage || "Modo solo lectura.") : ""}
                      >
                        Registrar
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </form>

          <div className="mt-6">
            <h3 className="text-sm font-semibold mb-2">Productos registrados</h3>
            <div className="border rounded overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Objeto del Gasto</th>
                    <th className="px-2 py-2 text-center">Cantidad</th>
                    <th className="px-2 py-2 text-center">Unidad</th>
                    <th className="px-2 py-2 text-center">Unitario</th>
                    <th className="px-2 py-2 text-center">Total</th>
                    <th className="px-2 py-2 text-left">Descripción</th>
                    <th className="px-2 py-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td className="px-2 py-3 text-center text-gray-500" colSpan={7}>
                        Aún no has agregado partidas.
                      </td>
                    </tr>
                  ) : (
                    items.map((row, index) => {
                      const descDisplay = getNiceDescDisplay({
                        row,
                        descCache,
                        tenderId: currentTenderId,
                      });

                      return (
                        <tr key={row._cid ?? `tmp-${index}`} className="border-t">
                          <td className="px-2 py-2">
                            {row.product_display ||
                              labelFrom(products, row.product, getProductLabel) ||
                              "—"}
                          </td>
                          <td className="px-2 py-2 text-center">{row.quantity}</td>
                          <td className="px-2 py-2 text-center">
                            {row.unit_display ||
                              labelFrom(units, row.unit, getUnitLabel) ||
                              "—"}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {row.estimated_unit_cost === ""
                              ? "—"
                              : Number(row.estimated_unit_cost).toFixed(2)}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {row.estimated_total === ""
                              ? "—"
                              : Number(row.estimated_total).toFixed(2)}
                          </td>
                          <td className="px-2 py-2">
                            <NoTranslateText text={descDisplay} />
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex justify-center gap-2">
                              <button
                                type="button"
                                className={`px-2 py-1 rounded text-white ${
                                  disableTableActions
                                    ? "bg-blue-300"
                                    : "bg-blue-600 hover:bg-blue-700"
                                }`}
                                onClick={() => editRow(row)}
                                disabled={disableTableActions}
                                title={isReadOnly ? (editBlockedMessage || "Modo solo lectura.") : ""}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className={`px-2 py-1 rounded text-white ${
                                  disableTableActions
                                    ? "bg-red-300"
                                    : "bg-red-600 hover:bg-red-700"
                                }`}
                                onClick={() => deleteRow(row._cid)}
                                disabled={disableTableActions}
                                title={isReadOnly ? (editBlockedMessage || "Modo solo lectura.") : ""}
                              >
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {showQuotesAndAck && (
            <div className="mt-6">
              {disableQuotesPanel && (
                <div className="mb-3 border border-slate-300 bg-slate-50 text-slate-800 rounded p-3 text-sm">
                  Las cotizaciones están visibles en modo solo lectura. No puedes subir, reasignar ni eliminar archivos desde esta vista.
                </div>
              )}

              <div className={disableQuotesPanel ? "pointer-events-none opacity-75" : ""}>
                <RequisitionQuotesPanel
                  requisitionId={requisitionId}
                  items={items}
                  onDraftInvalidChange={onDraftInvalidChange}
                  readOnly={disableQuotesPanel}
                  canEditRequisition={!disableQuotesPanel}
                />
              </div>
            </div>
          )}

          {showQuotesAndAck && (
            <div className="mt-6 border rounded p-3 bg-gray-50">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={ackCostRealistic}
                  onChange={(e) => onAckCostRealisticChange(e.target.checked)}
                  disabled={disableAckCheckbox}
                />
                <span className="text-sm">
                  Confirmo que el <strong>costo aproximado pero realista</strong> ha
                  sido verificado (requisito para imprimir/exportar PDF).
                </span>
              </label>
            </div>
          )}

          {showRealAmountSection && (
            <div className="mt-6">
              {disableRealSection && (
                <div className="mb-3 border border-slate-300 bg-slate-50 text-slate-800 rounded p-3 text-sm">
                  La sección de monto real está disponible solo para edición cuando la requisición no está bloqueada.
                </div>
              )}

              <div className={disableRealSection ? "pointer-events-none opacity-75" : ""}>
                <RealAmountSection
                  items={items}
                  products={products}
                  units={units}
                  descCache={descCache}
                  currentTenderId={currentTenderId}
                  realAmountBase={realAmountBase}
                  realUnitById={realUnitById}
                  realTotalsById={realTotalsById}
                  handleRealUnitChange={handleRealUnitChange}
                  handleRealUnitBlur={handleRealUnitBlur}
                  qtyOf={qtyOf}
                  estimatedUnitOf={estimatedUnitOf}
                  estimatedTotalOf={estimatedTotalOf}
                  realRowTotalOf={realRowTotalOf}
                  realTotalSum={realTotalSum}
                  realAmountReason={realAmountReason}
                  onRealAmountReasonChange={onRealAmountReasonChange}
                  busyRealAmount={busyRealAmount}
                  disableRealAmountByAck={disableRealAmountByAck || disableRealSection}
                  disableRealByQuoteDraft={disableRealByQuoteDraft || disableRealSection}
                  onSaveRealTotals={onSaveRealTotals}
                  realAmountLogs={realAmountLogs}
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onPrev}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              >
                Anterior
              </button>

              <button
                type="button"
                onClick={onCancel}
                disabled={busyItemOp || busySave || busyRealAmount}
                className={`px-4 py-2 rounded ${
                  busyItemOp || busySave || busyRealAmount
                    ? "bg-slate-100"
                    : "bg-slate-200 hover:bg-slate-300"
                }`}
              >
                Cancelar
              </button>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={disablePrimarySave}
                onClick={onSaveAll}
                className={`px-4 py-2 rounded text-white ${
                  disablePrimarySave
                    ? "bg-green-400"
                    : "bg-green-600 hover:bg-green-700"
                }`}
                title={saveButtonTitle}
              >
                {busySave ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
